import React from "react";
import { AbsoluteFill, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { Bg, DISPLAY, INK, LIME, popIn, PURPLE, UI, WHITE, YELLOW } from "./theme";

/* ════════════════════════════════════════════════════════════════════════
   SCENE 4b — FEATURE 4 (frame 585-690 / 19.5-23s, 105 frame)
   "AI Assistant Owner via WA" — chat "Berapa omset hari ini?" → grafik nongol.
   ════════════════════════════════════════════════════════════════════════ */

const CHART = [38, 52, 44, 70, 58, 86, 96];

const Tail: React.FC<{ side: "left" | "right"; color: string }> = ({ side, color }) => (
  <div
    className="absolute top-4 h-6 w-6 border-4 border-black"
    style={{
      background: color,
      [side === "left" ? "left" : "right"]: -14,
      transform: "rotate(45deg)",
    }}
  />
);

export const Scene4bAI: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const head = popIn(frame, fps, 0, { damping: 10, stiffness: 300, mass: 0.9 });
  const ask = popIn(frame, fps, 14, { damping: 10, stiffness: 340, mass: 0.85 });
  const typing = frame >= 30 && frame < 50;
  const answer = popIn(frame, fps, 50, { damping: 10, stiffness: 320, mass: 0.85 });
  const chart = popIn(frame, fps, 66, { damping: 11, stiffness: 300, mass: 0.9 });
  const badge = popIn(frame, fps, 92, { damping: 9, stiffness: 380, mass: 0.85 });

  return (
    <AbsoluteFill style={{ fontFamily: UI, color: INK }}>
      <Bg color={PURPLE} pattern="stripes" patternColor={WHITE} patternOpacity={0.18}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-[40px]">
          {/* ── Headline ── */}
          <div
            className="flex flex-col items-center gap-3"
            style={{ transform: `scale(${head.scale})`, opacity: head.opacity }}
          >
            <div
              className="border-4 border-black bg-black px-7 py-1 text-[64px] uppercase leading-none text-[#B8FF2F] shadow-[8px_8px_0px_0px_rgba(255,255,255,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              AI ASSISTANT OWNER
            </div>
            <div
              className="border-4 border-black bg-white px-7 py-1 text-[60px] uppercase leading-none text-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              style={{ fontFamily: DISPLAY }}
            >
              VIA WHATSAPP
            </div>
          </div>

          {/* ── Panel chat ── */}
          <div className="w-[980px]">
            <div className="border-4 border-black bg-white shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex items-center gap-4 border-b-4 border-black bg-black px-5 py-3">
                <div className="flex h-14 w-14 items-center justify-center border-4 border-white bg-[#B8FF2F]">
                  <div className="text-[26px]" style={{ fontFamily: DISPLAY }}>
                    AI
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-[34px] uppercase leading-none text-white" style={{ fontFamily: DISPLAY }}>
                    PANGKASIN ASSISTANT
                  </div>
                  <div className="text-[24px] text-[#B8FF2F]">laporan real-time 24 jam</div>
                </div>
              </div>

              {/* body chat — pesan menempel ke bawah */}
              <div className="flex min-h-[840px] flex-col justify-end gap-6 bg-[#efe7dd] px-6 py-7">
                {/* pertanyaan owner */}
                <div
                  className="relative w-[600px] self-end"
                  style={{
                    transform: `scale(${ask.scale})`,
                    opacity: ask.opacity,
                    transformOrigin: "right bottom",
                  }}
                >
                  <div className="relative border-4 border-black bg-[#B8FF2F] px-5 py-3 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                    <Tail side="right" color={LIME} />
                    <div className="text-[32px] text-black">Berapa omset hari ini?</div>
                  </div>
                </div>

                {/* indikator mengetik */}
                {typing ? (
                  <div className="flex w-[170px] items-center gap-3 self-start border-4 border-black bg-white px-5 py-4">
                    {[0, 1, 2].map((i) => (
                      <div
                        key={i}
                        className="h-5 w-5 rounded-full bg-black"
                        style={{ transform: `translateY(${Math.sin((frame - 30) / 2 + i) * 6}px)` }}
                      />
                    ))}
                  </div>
                ) : null}

                {/* jawaban AI */}
                <div
                  className="relative w-[700px] self-start"
                  style={{
                    transform: `scale(${answer.scale})`,
                    opacity: answer.opacity,
                    transformOrigin: "left bottom",
                  }}
                >
                  <div className="relative border-4 border-black bg-white px-5 py-3 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                    <Tail side="left" color={WHITE} />
                    <div className="text-[32px] text-black">
                      Omset hari ini <span style={{ fontFamily: DISPLAY }}>Rp 1.240.000</span> dari 32 transaksi
                    </div>
                  </div>
                </div>

                {/* kartu grafik */}
                <div
                  className="w-[860px] self-start border-4 border-black bg-[#FFFDF5] shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                  style={{
                    transform: `scale(${chart.scale})`,
                    opacity: chart.opacity,
                    transformOrigin: "left bottom",
                  }}
                >
                  <div className="flex items-center justify-between border-b-4 border-black bg-[#22E0FF] px-4 py-2">
                    <div className="text-[28px] uppercase" style={{ fontFamily: DISPLAY }}>
                      OMSET 7 HARI
                    </div>
                    <div className="border-4 border-black bg-white px-3 py-1 text-[24px]" style={{ fontFamily: DISPLAY }}>
                      NAIK 18%
                    </div>
                  </div>
                  <div className="px-5 py-4">
                    <div className="flex h-[250px] items-end gap-4">
                      {CHART.map((h, i) => {
                        const g = spring({
                          frame: frame - (72 + i * 3),
                          fps,
                          config: { damping: 12, stiffness: 220, mass: 0.7 },
                        });
                        return (
                          <div
                            key={i}
                            className="flex-1 border-4 border-black"
                            style={{
                              height: `${Math.min(h * g, 100)}%`,
                              background: i === CHART.length - 1 ? "#FF2E88" : "#FFE14D",
                            }}
                          />
                        );
                      })}
                    </div>
                    <div className="mt-3 text-[24px] text-black/60">Sen · Sel · Rab · Kam · Jum · Sab · Min</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Badge ── */}
          <div
            className="border-4 border-black bg-[#B8FF2F] px-7 py-3 text-[34px] uppercase shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
            style={{ fontFamily: DISPLAY, transform: `scale(${badge.scale}) rotate(-1.6deg)`, opacity: badge.opacity }}
          >
            TANYA APA SAJA · DIJAWAB INSTAN
          </div>
        </div>
      </Bg>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
